import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import NoData from '../../../components/common/NoData';
import AdminPage from '../../../components/admin/AdminPage';
import DataTable from '../../../components/admin/DataTable';
import { getClassroomDetails, getAllTrainers, createClassroom, updateClassroomDetails, updateClassroomStatus, deleteClassroom } from '../../../services/adminService';
import Loader from '../../../components/common/Loader';
import { encodeData, decodeData } from '../../../utils/encodeAndEncode';
import { usePermissions } from '../../../context/PermissionsContext';
import QuestionBank from '../questionBank/QuestionBank';

const course1 = '/images/admin/course/course1.png';

function AllClassroom({ defaultTab = 'sessions' }) {
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteClassroomData, setDeleteClassroomData] = useState(null);
    const [editingClassroom, setEditingClassroom] = useState(null);
    const [formData, setFormData] = useState({
        classroomName: '',
        description: '',
        collaborationName: '',
        uploadFile: null,
        bannerPreview: null,
        selectedTrainers: []
    });

    const [formErrors, setFormErrors] = useState({
        classroomName: '',
        description: '',
        collaborationName: '',
        uploadFile: ''
    });

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const user_id = user?.id;
    // console.log('User ID:', user_id);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [AllClassroomData, setAllClassroomData] = useState([]);
    const [AllTrainersData, setAllTrainersData] = useState([]);

    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [statusFilter, setStatusFilter] = useState('all');
    const [statusLoading, setStatusLoading] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(
        defaultTab === 'questionBank' ? 'questionBank' : 'sessions'
    );

    const getAllClassroomData = async () => {
        try {
            const response = await getClassroomDetails({
                page: currentPage,
                limit: itemsPerPage,
                search: searchQuery,
                user_id: user_id
            });
            // console.log('Classroom data Response', response);
            if (response.success && response.data) {
                setAllClassroomData(response.data.classes || []);
                setTotalPages(response.data.pagination?.totalPages || 1);
                setTotalRecords(response.data.pagination?.totalRecords || 0);
            }
        } catch (error) {
            // console.log(error);
            toast.error('Failed to fetch live sessions', {
                position: "top-center"
            });
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    };

    const getAllTrainersData = async () => {
        try {
            const response = await getAllTrainers();
            // console.log('Trainers data Response', response);
            if (response.success) {
                setAllTrainersData(response.trainers || []);
            }
        } catch (error) {
            // toast.error(error.response?.data?.message || 'Failed to fetch trainers', {
            //     position: "top-center"
            // });
            // console.log('Trainers data error', error);
        }
    }

    useEffect(() => {
        const minLoadTime = 500; // Minimum loading time in milliseconds
        const startTime = Date.now();

        getAllClassroomData().then(() => {
            // Calculate remaining time to meet minimum loading duration
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime < minLoadTime) {
                setTimeout(() => {
                    setLoading(false);
                }, minLoadTime - elapsedTime);
            }
        });
        getAllTrainersData();
    }, [currentPage, itemsPerPage, searchQuery]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        setFormErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const handleBannerUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 50 * 1024 * 1024) { // 50MB
                alert('File size should not exceed 50MB');
                return;
            }
            setFormData(prev => ({
                ...prev,
                uploadFile: file,
                bannerPreview: URL.createObjectURL(file)
            }));
            // Clear error when file is uploaded
            setFormErrors(prev => ({
                ...prev,
                uploadFile: ''
            }));
        }
    };

    const handleRemoveBanner = () => {
        if (formData.bannerPreview && formData.bannerPreview.startsWith('blob:')) {
            URL.revokeObjectURL(formData.bannerPreview);
        }
        setFormData(prev => ({
            ...prev,
            uploadFile: null,
            bannerPreview: null
        }));
        // Clear the upload file error when banner is removed, but it will be required again
        setFormErrors(prev => ({
            ...prev,
            uploadFile: ''
        }));
    };

    const handleTrainerSelect = (e) => {
        const trainerId = parseInt(e.target.value);
        if (trainerId) {
            const trainer = AllTrainersData.find(t => t.user_id === trainerId);
            if (trainer && !formData.selectedTrainers.some(t => t.user_id === trainer.user_id)) {
                setFormData(prev => ({
                    ...prev,
                    selectedTrainers: [...prev.selectedTrainers, trainer]
                }));
            }
            // Reset the select element to default option
            e.target.value = '';
        }
    };

    const handleRemoveTrainer = (trainerId) => {
        setFormData(prev => ({
            ...prev,
            selectedTrainers: prev.selectedTrainers.filter(t => t.user_id !== trainerId)
        }));
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1); // Reset to first page when search changes
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleLimitChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1); // Reset to first page when limit changes
    };

    const handleStatusFilterChange = (e) => {
        setStatusFilter(e.target.value);
    };

    // Filter classrooms based on status
    const filteredClassrooms = useMemo(() => {
        if (statusFilter === 'all') {
            return AllClassroomData;
        }
        return AllClassroomData.filter(classroom => {
            if (statusFilter === 'active') {
                return classroom.is_active === 1;
            } else if (statusFilter === 'inactive') {
                return classroom.is_active === 0;
            }
            return true;
        });
    }, [AllClassroomData, statusFilter]);

    const handleCreateClassroom = async () => {
        // Validate form
        const errors = {};
        if (!formData.classroomName.trim()) {
            errors.classroomName = 'Live Session Title is required';
        }
        if (!formData.description.trim()) {
            errors.description = 'Live Session Description is required';
        }
        if (!formData.collaborationName.trim()) {
            errors.collaborationName = 'Collaboration Name is required';
        }
        if (!formData.uploadFile) {
            errors.uploadFile = 'Banner image is required';
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        try {
            setIsSubmitting(true);
            setLoading(true);

            // Create FormData object for API call
            const classroomForm = new FormData();
            classroomForm.append("cls_name", formData.classroomName);
            classroomForm.append("cls_desc", formData.description);
            classroomForm.append("collaboration_name", formData.collaborationName);
            classroomForm.append("banner_img", formData.uploadFile);
            classroomForm.append("trainers", JSON.stringify(formData.selectedTrainers));
            classroomForm.append("page", currentPage);
            classroomForm.append("limit", itemsPerPage);
            classroomForm.append("search", searchQuery);
            classroomForm.append("user_id", localStorage.getItem('user_id'));

            const response = await createClassroom(classroomForm);
            // console.log('API Response:', response);

            if (response.success) {
                toast.success('Live session created successfully!', {
                    position: "top-center"
                });
        setShowCreateModal(false);
        setFormData({
                    classroomName: '',
            description: '',
            collaborationName: '',
                    uploadFile: null,
            bannerPreview: null,
            selectedTrainers: []
        });
                setFormErrors({
                    classroomName: '',
                    description: '',
                    collaborationName: '',
                    uploadFile: ''
                });
                await getAllClassroomData();
            } else {
                toast.error(response.message || 'Failed to create live session', {
                    position: "top-center"
                });
            }
        } catch (error) {
            console.error('Error creating classroom:', error);
            toast.error(error.response?.data?.message || 'Failed to create live session', {
                position: "top-center"
            });
        } finally {
            setIsSubmitting(false);
            setLoading(false);
        }
    };

    const handleNavigateToClassroom = (classroomId) => {
        if (!permissions.classroom_management_view_analytics) {
            toast.warning("You don't have permission to view live session analytics");
            return;
        }
        const encodedClassroomId = encodeData(classroomId);
        navigate(`/admin/live-sessions/classroom-dashboard/${encodedClassroomId}`);
    };

    const handleNavigateToCreateClassroom = () => {
        if (!permissions.classroom_management_create) {
            toast.warning("You don't have permission to create live sessions");
            return;
        }
        setShowCreateModal(true);
    };

    const handleEditClassroom = (classroom) => {
        if (!permissions.classroom_management_edit) {
            toast.warning("You don't have permission to edit live sessions");
            return;
        }
        setEditingClassroom(classroom);
        setFormData({
            classroomName: classroom.cls_name,
            description: classroom.cls_desc,
            collaborationName: classroom.collaboration_name,
            uploadFile: null,
            bannerPreview: classroom.banner_img,
            selectedTrainers: classroom.trainers || []
        });
        setShowEditModal(true);
    };

    const handleUpdateClassroom = async () => {
        // Validate form
        const errors = {};
        if (!formData.classroomName.trim()) {
            errors.classroomName = 'Live Session Title is required';
        }
        if (!formData.description.trim()) {
            errors.description = 'Live Session Description is required';
        }
        if (!formData.collaborationName.trim()) {
            errors.collaborationName = 'Collaboration Name is required';
        }
        if (!formData.bannerPreview && !formData.uploadFile) {
            errors.uploadFile = 'Banner image is required';
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        try {
            setIsSubmitting(true);
            setLoading(true);

            const classroomForm = new FormData();
            classroomForm.append("class_id", editingClassroom.id);
            classroomForm.append("user_id", user_id);
            classroomForm.append("cls_name", formData.classroomName);
            classroomForm.append("cls_desc", formData.description);
            classroomForm.append("collaboration_name", formData.collaborationName);
            classroomForm.append("trainers", JSON.stringify(formData.selectedTrainers));

            if (formData.uploadFile) {
                classroomForm.append("banner_img", formData.uploadFile);
            }

            const response = await updateClassroomDetails(classroomForm);
            // console.log('Update API Response:', response);

            if (response && (response.data?.success || response.status === 200 || response.success)) {
                toast.success('Live session updated successfully!', {
                    position: "top-center"
                });
                setShowEditModal(false);
                setEditingClassroom(null);
                setFormData({
                    classroomName: '',
                    description: '',
                    collaborationName: '',
                    uploadFile: null,
                    bannerPreview: null,
                    selectedTrainers: []
                });
                setFormErrors({
                    classroomName: '',
                    description: '',
                    collaborationName: '',
                    uploadFile: ''
                });
                await getAllClassroomData();
            } else {
                toast.error(response?.data?.message || response?.message || 'Failed to update live session', {
                    position: "top-center"
                });
            }
        } catch (error) {
            console.error('Error updating classroom:', error);
            toast.error(error.response?.data?.message || 'Failed to update live session', {
                position: "top-center"
            });
        } finally {
            setIsSubmitting(false);
            setLoading(false);
        }
    };

    const handleToggleStatus = async (e, classroom) => {
        e.stopPropagation();
        if (!permissions.classroom_management_status) {
            toast.warning("You don't have permission to change live session status");
            return;
        }

        try {
            setStatusLoading(classroom.id);
            setLoading(true);

            const newStatus = classroom.is_active === 1 ? 0 : 1;
            const payload = {
                class_id: classroom.id,
                status: newStatus
            };

            const response = await updateClassroomStatus(payload);

            if (response.success) {
                toast.success(`Live session ${newStatus ? 'activated' : 'deactivated'} successfully!`, {
                    position: "top-center"
                });
                await getAllClassroomData();
            } else {
                toast.error(response.message || 'Failed to update live session status', {
                    position: "top-center"
                });
            }
        } catch (error) {
            console.error('Error updating classroom status:', error);
            toast.error(error.response?.data?.message || 'Failed to update live session status', {
                position: "top-center"
            });
        } finally {
            setStatusLoading(null);
            setLoading(false);
        }
    };

    const handleDeleteClick = (e, classroomId, classroomName) => {
        e.stopPropagation();
        if (!permissions.classroom_management_delete) {
            toast.warning("You don't have permission to delete live sessions");
            return;
        }
        setDeleteClassroomData({ id: classroomId, name: classroomName });
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteClassroomData) return;

        try {
            setLoading(true);
            const response = await deleteClassroom({
                classroom_id: deleteClassroomData.id
            });

            if (response.success) {
                toast.success('Live session deleted successfully!', {
                    position: "top-center"
                });
                await getAllClassroomData();
            } else {
                toast.error(response.message || 'Failed to delete live session', {
                    position: "top-center"
                });
            }
        } catch (error) {
            console.error('Error deleting classroom:', error);
            toast.error(error.response?.data?.message || 'Failed to delete classroom', {
                position: "top-center"
            });
        } finally {
            setShowDeleteModal(false);
            setDeleteClassroomData(null);
            setLoading(false);
        }
    };

    // -- Get status class
    const getStatusClass = (isActive) => {
        return isActive === 1 ? 'bg-green-50 border border-green-500 text-green-500 p-2' : 'bg-red-50 border border-red-500 text-red-500 p-2';
    }

    const getStatusText = (isActive) => {
        return isActive === 1 ? 'Active' : 'Inactive';
    }

    return (
        <AdminPage
            title="Live Sessions"
            description="Manage live sessions. Create sessions, add trainees, and track assignments."
            actions={
                activeTab === 'sessions' && permissions.classroom_management_create && (
                    <button
                        type="button"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#7083FF] hover:bg-[#5f73e6] text-white font-medium text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        onClick={handleNavigateToCreateClassroom}
                        title="Create Live Session"
                    >
                        <Icon icon="fluent:add-24-regular" width="18" height="18" />
                        <span>Create Live Session</span>
                    </button>
                )
            }
        >
            <div className="w-full">
                {/* Tab Navigation and Search */}
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => setActiveTab('sessions')}
                            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${
                                activeTab === 'sessions'
                                    ? 'bg-[#7083FF] text-white border-[#7083FF] shadow-sm'
                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                            <Icon icon="fluent:video-person-24-regular" width={18} height={18} className="shrink-0" />
                            <span>Live Sessions</span>
                        </button>
                        {permissions.question_bank_management_view && (
                            <button
                                type="button"
                                onClick={() => setActiveTab('questionBank')}
                                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${
                                    activeTab === 'questionBank'
                                        ? 'bg-[#7083FF] text-white border-[#7083FF] shadow-sm'
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <Icon icon="fluent:quiz-new-24-regular" width={18} height={18} className="shrink-0" />
                                <span>Question Bank</span>
                            </button>
                        )}
                    </div>

                    {/* Search bar */}
                    {activeTab === 'sessions' && (
                        <div className="relative w-full sm:w-64">
                            <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="search"
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:border-[#7083FF]"
                                placeholder="Search Live Sessions..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                        </div>
                    )}
                </div>

                {/* Tab content */}
                <div className="min-h-[360px] pt-3 sm:pt-4">
                    {activeTab === 'questionBank' ? (
                        <QuestionBank />
                    ) : (
                <>
                    <DataTable
                        loading={loading}
                        hasRows={filteredClassrooms.length > 0}
                        caption="Loading Live Sessions..."
                        minHeight="400px"
                        emptyMessage="No live sessions found"
                    >
                        <table className="w-full border-collapse mb-0 text-sm" style={{ minWidth: '800px' }}>
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100">
                                    <th className="px-4 py-2.5 text-left font-semibold text-gray-600 w-[40%]">
                                        Live Session
                                    </th>
                                    <th className="px-4 py-2.5 text-left font-semibold text-gray-600 w-[120px]">
                                        Trainees
                                    </th>
                                    <th className="px-4 py-2.5 text-left font-semibold text-gray-600 w-[120px]">
                                        Trainers
                                    </th>
                                    <th className="px-4 py-2.5 text-left font-semibold text-gray-600 w-[120px]">
                                        Status
                                    </th>
                                    <th className="px-4 py-2.5 text-right font-semibold text-gray-600 w-[160px]">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClassrooms.map((classroom) => (
                                    <tr
                                        key={classroom.id}
                                        className={`border-b last:border-b-0 ${permissions.classroom_management_view_analytics ? 'cursor-pointer hover:bg-gray-50/70' : ''}`}
                                        onClick={() => handleNavigateToClassroom(classroom.id)}
                                        style={{ cursor: permissions.classroom_management_view_analytics ? 'pointer' : 'default' }}
                                        title={!permissions.classroom_management_view_analytics ? "You don't have permission to view live session analytics" : "Click to view live session analytics"}
                                    >
                                        <td className="px-4 py-3 align-top">
                                            <div className="flex items-center gap-4">
                                                <img
                                                    src={classroom.banner_img || course1}
                                                    alt={classroom.cls_name}
                                                    className="rounded-xl border border-gray-100 object-cover w-[160px] h-[96px] shadow-sm"
                                                />
                                                <div className="min-w-0">
                                                    <div className="font-medium text-gray-900 mb-1 line-clamp-2">
                                                        {classroom.cls_name}
                                                    </div>
                                                    <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <Icon icon="fluent:clipboard-task-24-regular" className="mr-2" width="16" height="16" />
                                                            {classroom.total_assignments || 0} Assignments
                                                        </span>
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <Icon icon="fluent:quiz-new-24-regular" className="mr-2" width="16" height="16" />
                                                            {classroom.total_assessments || 0} Assessments
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-wrap gap-4 mt-1 text-xs text-gray-600">
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <Icon icon="fluent:video-clip-24-regular" className="mr-2" width="16" height="16" />
                                                            {classroom.total_recorded || 0} Recorded
                                                        </span>
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <Icon icon="fluent:video-person-24-regular" className="mr-2" width="16" height="16" />
                                                            {classroom.total_live_classes || 0} Live Classes
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 align-middle text-start">
                                            <div className="flex items-center gap-1.5 text-gray-700">
                                                <Icon icon="fluent:people-24-regular" className="mr-2" width="16" height="16" />
                                                {classroom.total_trainees || 0}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 align-middle text-start">
                                            <div className="flex items-center gap-1.5 text-gray-700">
                                                <Icon icon="fluent:person-24-regular" className="mr-2" width="16" height="16" />
                                                {classroom.total_trainers || 0}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 align-middle text-start">
                                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(classroom.is_active)}`}>
                                                {getStatusText(classroom.is_active)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 align-middle">
                                            <div className="flex items-center justify-end gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={!permissions.classroom_management_edit ? "You don't have permission to edit live sessions" : "Edit Live Session"}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditClassroom(classroom);
                                                    }}
                                                    disabled={!permissions.classroom_management_edit}
                                                >
                                                    <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={!permissions.classroom_management_delete ? "You don't have permission to delete live sessions" : "Delete Live Session"}
                                                    onClick={(e) => handleDeleteClick(e, classroom.id, classroom.cls_name)}
                                                    disabled={!permissions.classroom_management_delete}
                                                >
                                                    <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-lg ${classroom.is_active === 1 ? "bg-green-500 hover:bg-green-600 text-white" : "border border-yellow-500 text-yellow-700 hover:bg-yellow-100"} disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    title={!permissions.classroom_management_status ? "You don't have permission to change status" : `${classroom.is_active === 1 ? 'Deactivate' : 'Activate'} Live Session`}
                                                    onClick={(e) => handleToggleStatus(e, classroom)}
                                                    disabled={statusLoading === classroom.id || !permissions.classroom_management_status}
                                                >
                                                    {statusLoading === classroom.id ? (
                                                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <Icon
                                                            icon={classroom.is_active === 1 ? "fluent:checkmark-circle-24-regular" : "fluent:dismiss-circle-24-regular"}
                                                            width="18"
                                                            height="18"
                                                        />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </DataTable>

                    {/* Record per page and pagination – match AllCourses */}
                    <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                            <span className="hidden sm:inline">Rows per page:</span>
                            <select
                                className="px-3 py-1.5 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
                                value={itemsPerPage}
                                onChange={handleLimitChange}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span className="text-gray-500">
                                {filteredClassrooms.length === 0
                                    ? 'No records'
                                    : `Showing ${(currentPage - 1) * itemsPerPage + 1}–${Math.min(
                                        currentPage * itemsPerPage,
                                        filteredClassrooms.length
                                      )} of ${filteredClassrooms.length}`}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || Math.ceil(filteredClassrooms.length / itemsPerPage) === 0}
                                title="Previous page"
                            >
                                <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
                            </button>
                            <span className="px-2 text-gray-700">
                                Page <span className="font-medium">{filteredClassrooms.length === 0 ? 0 : currentPage}</span> of{' '}
                                <span className="font-medium">{Math.ceil(filteredClassrooms.length / itemsPerPage) || 0}</span>
                            </span>
                            <button
                                type="button"
                                className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === Math.ceil(filteredClassrooms.length / itemsPerPage) || Math.ceil(filteredClassrooms.length / itemsPerPage) === 0}
                                title="Next page"
                            >
                                <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
                            </button>
                        </div>
                    </div>

                    {/* Create Live Session Modal */}
                    {showCreateModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="px-5 py-3.5 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                            <h5 className="font-semibold text-base">Create New Live Session</h5>
                            <button
                                type="button"
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => setShowCreateModal(false)}
                            >
                                <Icon icon="fluent:dismiss-24-regular" width="20" height="20" />
                            </button>
                        </div>
                        <div className="p-5">
                                {/* Title */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Live Session Title*</label>
                                    <input
                                        type="text"
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.classroomName ? 'border-red-500' : 'border-input-border'}`}
                                        name="classroomName"
                                        value={formData.classroomName}
                                        onChange={handleInputChange}
                                        placeholder="Enter live session title"
                                        maxLength={100}
                                    />
                                    {formErrors.classroomName ? (
                                        <div className="text-red-500 text-sm mt-1">{formErrors.classroomName}</div>
                                    ) : (
                                        <small className="text-gray-500">Maximum 100 characters allowed ({100 - formData.classroomName.length} remaining)</small>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Live Session Description*</label>
                                    <textarea
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.description ? 'border-red-500' : 'border-input-border'}`}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="Enter live session description"
                                        maxLength={500}
                                    />
                                    {formErrors.description ? (
                                        <div className="text-red-500 text-sm mt-1">{formErrors.description}</div>
                                    ) : (
                                        <small className="text-gray-500">Maximum 500 characters allowed ({500 - formData.description.length} remaining)</small>
                                    )}
                                </div>

                                {/* Collaboration Name */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Collaboration Name (Chat Comming soon)*</label>
                                    <input
                                        type="text"
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.collaborationName ? 'border-red-500' : 'border-input-border'}`}
                                        name="collaborationName"
                                        value={formData.collaborationName}
                                        onChange={handleInputChange}
                                        placeholder="Enter Collaboration Name"
                                        maxLength={100}
                                    />
                                    {formErrors.collaborationName ? (
                                        <div className="text-red-500 text-sm mt-1">{formErrors.collaborationName}</div>
                                    ) : (
                                        <small className="text-gray-500">Maximum 100 characters allowed ({100 - formData.collaborationName.length} remaining)</small>
                                    )}
                                </div>

                                {/* Banner Upload */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload Banner*</label>
                                    {!formData.bannerPreview ? (
                                        <div
                                            className={`relative flex flex-col items-center justify-center w-full rounded-lg border-2 border-dashed ${formErrors.uploadFile ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-gray-50'} px-4 py-6 text-center cursor-pointer transition hover:bg-gray-100`}
                                            onClick={() => document.getElementById('bannerUpload').click()}
                                        >
                                            <input
                                                type="file"
                                                id="bannerUpload"
                                                className="hidden"
                                                onChange={handleBannerUpload}
                                                accept="image/*"
                                            />
                                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-2">
                                                <Icon icon="fluent:image-add-24-regular" width="24" height="24" />
                                            </div>
                                            <div className="text-sm font-medium text-gray-900 mb-1">Click to upload banner</div>
                                            <div className="text-xs text-gray-500">PNG, JPG up to 50MB</div>
                                            {formErrors.uploadFile && (
                                                <div className="text-red-500 text-xs mt-2">{formErrors.uploadFile}</div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="relative rounded-lg overflow-hidden border border-gray-200">
                                            <img
                                                src={formData.bannerPreview}
                                                alt="Banner Preview"
                                                className="w-full h-48 object-cover"
                                            />
                                            <button
                                                className="absolute top-2 right-2 p-2 rounded-lg bg-white/90 hover:bg-white text-red-500 hover:text-red-600 shadow-md transition-colors"
                                                onClick={handleRemoveBanner}
                                                type="button"
                                                title="Remove banner"
                                            >
                                                <Icon icon="fluent:delete-24-regular" width="20" height="20" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Trainer Selection */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Select Trainers</label>
                                    <select
                                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        onChange={handleTrainerSelect}
                                        defaultValue=""
                                    >
                                        <option value="">Choose trainer...</option>
                                        {AllTrainersData
                                            .filter(trainer => !formData.selectedTrainers.some(t => t.user_id === trainer.user_id))
                                            .map(trainer => (
                                                <option key={trainer.user_id} value={trainer.user_id}>
                                                    {trainer.name}
                                                </option>
                                            ))
                                        }
                                    </select>

                                    {/* Selected Trainers Display */}
                                    {formData.selectedTrainers.length > 0 && (
                                        <div className="trainers-container mt-2">
                                            {formData.selectedTrainers.map(trainer => (
                                                <div key={trainer.user_id} className="selected-trainer">
                                                    <span className="flex items-center">
                                                        <Icon icon="fluent:person-24-regular" className="mr-2" width="16" height="16" />
                                                        {trainer.name}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="remove-trainer"
                                                        onClick={() => handleRemoveTrainer(trainer.user_id)}
                                                        title="Remove trainer"
                                                    >
                                                        <Icon icon="fluent:dismiss-24-filled" width="16" height="16" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2 bg-gray-50">
                                <button
                                    type="button"
                                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-3.5 rounded-lg text-sm transition-colors"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        handleRemoveBanner();
                                        setFormData({
                                            classroomName: '',
                                            description: '',
                                            collaborationName: '',
                                            uploadFile: null,
                                            bannerPreview: null,
                                            selectedTrainers: []
                                        });
                                        setFormErrors({
                                            classroomName: '',
                                            description: '',
                                            collaborationName: '',
                                            uploadFile: ''
                                        });
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-3.5 rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                                    onClick={handleCreateClassroom}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            Creating...
                                        </>
                                    ) : (
                                        'Create Live Session'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Edit Live Session Modal */}
                    {showEditModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="px-5 py-3.5 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                            <h5 className="font-semibold text-base">Edit Live Session</h5>
                            <button
                                type="button"
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => {
                                    setShowEditModal(false);
                                    setEditingClassroom(null);
                                    handleRemoveBanner();
                                    setFormData({
                                        classroomName: '',
                                        description: '',
                                        collaborationName: '',
                                        uploadFile: null,
                                        bannerPreview: null,
                                        selectedTrainers: []
                                    });
                                    setFormErrors({
                                        classroomName: '',
                                        description: '',
                                        collaborationName: '',
                                        uploadFile: ''
                                    });
                                }}
                            >
                                <Icon icon="fluent:dismiss-24-regular" width="20" height="20" />
                            </button>
                        </div>
                        <div className="p-5">
                                {/* Title */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-font-primary mb-1.5">Classroom Title*</label>
                                    <input
                                        type="text"
                                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.classroomName ? 'border-red-500' : ''}`}
                                        name="classroomName"
                                        value={formData.classroomName}
                                        onChange={handleInputChange}
                                        placeholder="Enter live session title"
                                        maxLength={100}
                                    />
                                    {formErrors.classroomName ? (
                                        <div className="text-red-500 text-sm mt-1">{formErrors.classroomName}</div>
                                    ) : (
                                        <small className="text-gray-500">Maximum 100 characters allowed ({100 - formData.classroomName.length} remaining)</small>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-font-primary mb-1.5">Live Session Description*</label>
                                    <textarea
                                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.description ? 'border-red-500' : ''}`}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="Enter live session description"
                                        maxLength={500}
                                    />
                                    {formErrors.description ? (
                                        <div className="text-red-500 text-sm mt-1">{formErrors.description}</div>
                                    ) : (
                                        <small className="text-gray-500">Maximum 500 characters allowed ({500 - formData.description.length} remaining)</small>
                                    )}
                                </div>

                                {/* Collaboration Name */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-font-primary mb-1.5">Collaboration Name (Chat)*</label>
                                    <input
                                        type="text"
                                        className={`w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${formErrors.collaborationName ? 'border-red-500' : ''}`}
                                        name="collaborationName"
                                        value={formData.collaborationName}
                                        onChange={handleInputChange}
                                        placeholder="Enter collaboration name"
                                        maxLength={100}
                                    />
                                    {formErrors.collaborationName ? (
                                        <div className="text-red-500 text-sm mt-1">{formErrors.collaborationName}</div>
                                    ) : (
                                        <small className="text-gray-500">Maximum 100 characters allowed ({100 - formData.collaborationName.length} remaining)</small>
                                    )}
                                </div>

                                {/* Banner Upload */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-font-primary mb-1.5">Upload Banner*</label>
                                    {!formData.bannerPreview ? (
                                        <div
                                            className={`relative flex flex-col items-center justify-center w-full rounded-lg border-2 border-dashed ${formErrors.uploadFile ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-gray-50'} px-4 py-6 text-center cursor-pointer transition hover:bg-gray-100`}
                                            onClick={() => document.getElementById('editBannerUpload').click()}
                                        >
                                            <input
                                                type="file"
                                                id="editBannerUpload"
                                                className="hidden"
                                                onChange={handleBannerUpload}
                                                accept="image/*"
                                            />
                                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-2">
                                                <Icon icon="fluent:image-add-24-regular" width="24" height="24" />
                                            </div>
                                            <div className="text-sm font-medium text-gray-900 mb-1">Click to upload banner</div>
                                            <div className="text-xs text-gray-500">PNG, JPG up to 50MB</div>
                                            {formErrors.uploadFile && (
                                                <div className="text-red-500 text-xs mt-2">{formErrors.uploadFile}</div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="relative rounded-lg overflow-hidden border border-gray-200">
                                            <img
                                                src={formData.bannerPreview}
                                                alt="Banner Preview"
                                                className="w-full h-48 object-cover"
                                            />
                                            <button
                                                className="absolute top-2 right-2 p-2 rounded-lg bg-white/90 hover:bg-white text-red-500 hover:text-red-600 shadow-md transition-colors"
                                                onClick={handleRemoveBanner}
                                                type="button"
                                                title="Remove banner"
                                            >
                                                <Icon icon="fluent:delete-24-regular" width="20" height="20" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Trainer Selection */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Select Trainers</label>
                                    <select
                                        className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                        onChange={handleTrainerSelect}
                                        defaultValue=""
                                    >
                                        <option value="">Choose trainer...</option>
                                        {AllTrainersData
                                            .filter(trainer => !formData.selectedTrainers.some(t => t.user_id === trainer.user_id))
                                            .map(trainer => (
                                                <option key={trainer.user_id} value={trainer.user_id}>
                                                    {trainer.name}
                                                </option>
                                            ))
                                        }
                                    </select>

                                    {/* Selected Trainers Display */}
                                    {formData.selectedTrainers.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {formData.selectedTrainers.map(trainer => (
                                                <div key={trainer.user_id} className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                                                    <Icon icon="fluent:person-24-filled" className="text-blue-600" width="16" height="16" />
                                                    <span className="text-gray-700 font-medium">{trainer.name}</span>
                                                    <button
                                                        type="button"
                                                        className="ml-1 text-gray-400 hover:text-red-500 transition-colors"
                                                        onClick={() => handleRemoveTrainer(trainer.user_id)}
                                                        title="Remove trainer"
                                                    >
                                                        <Icon icon="fluent:dismiss-circle-24-filled" width="18" height="18" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2 bg-gray-50">
                                <button
                                    type="button"
                                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-3.5 rounded-lg text-sm transition-colors"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setEditingClassroom(null);
                                        handleRemoveBanner();
                                        setFormData({
                                            classroomName: '',
                                            description: '',
                                            collaborationName: '',
                                            uploadFile: null,
                                            bannerPreview: null,
                                            selectedTrainers: []
                                        });
                                        setFormErrors({
                                            classroomName: '',
                                            description: '',
                                            collaborationName: '',
                                            uploadFile: ''
                                        });
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-3.5 rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                                    onClick={handleUpdateClassroom}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Live Session'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {showDeleteModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                        <div className="px-5 py-3.5 border-b border-gray-100 flex justify-between items-center">
                            <h5 className="font-semibold text-base">Delete Live Session</h5>
                            <button
                                type="button"
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteClassroomData(null);
                                }}
                            >
                                <Icon icon="fluent:dismiss-24-regular" width="20" height="20" />
                            </button>
                        </div>
                        <div className="p-5">
                                <div className="text-center">
                                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                                        <Icon icon="fluent:delete-24-regular" className="text-red-500" width="24" height="24" />
                                    </div>
                                    <h5 className="text-base font-semibold mb-2">Are you sure?</h5>
                                    <p className="text-gray-500 text-sm">
                                        Do you really want to delete the live session "{deleteClassroomData?.name}"? This action cannot be undone.
                                    </p>
                                </div>
                            </div>
                            <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2 bg-gray-50">
                                <button
                                    type="button"
                                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-3.5 rounded-lg text-sm transition-colors"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setDeleteClassroomData(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-3.5 rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                                    onClick={handleDeleteConfirm}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Icon icon="fluent:delete-24-regular" width="16" height="16" />
                                            Delete
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                    )}
                </>
                )}
                </div>
            </div>
        </AdminPage>
    );
}

export default AllClassroom

